import {UserModel} from 'app/models/UserModel'
import Avatar from 'common/components/Avatar'
import {toMessagePreview} from 'common/components/chat/components/utils/messageText'
import moment from 'moment'
import {FC, useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, useNavigate} from 'react-router'
import {RootState} from 'setup'
import {getUserMessages} from 'setup/redux/Actions/User/user'
import socket from 'socket'

import {toAbsoluteUrl} from '_metronic/helpers'

const Message: FC<{
  user: UserModel
  message: {
    latest_message_sender_id: number
    latest_message: string
    latest_message_created_at: string
    sender: {name: string; doc_id: string | number; photo?: string}
    thread_id: number
    thread_name?: string
  }
}> = ({user, message}) => {
  const navigate = useNavigate()

  const messageFrom = message.latest_message_sender_id == user.id ? 'You' : message.sender.name
  const latestMessage = toMessagePreview(message.latest_message)

  return (
    <div>
      <div
        className='d-flex flex-stack py-4 cursor-pointer'
        onClick={() => navigate(`/messages/${message.thread_id}`)}
      >
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-45px symbol-circle'>
            <Avatar
              size='45'
              label={message.thread_name?.substring(0, 1) || 'N/A'}
              colorHashKey={`${message.thread_id}-${message.thread_name}`}
              circle
              showLabel={true}
            />
          </div>

          <div className='ms-5'>
            <Link
              to={`/messages/${message.thread_id}`}
              className='fs-5 fw-bolder text-gray-900 text-hover-primary mb-2'
            >
              {message.thread_name}
            </Link>
            <div className='fw-bold text-gray-400'>{`${messageFrom}: ${latestMessage}`}</div>
          </div>
        </div>

        <div className='d-flex flex-column align-items-end ms-2'>
          <span className='text-muted fs-7 mb-1'>
            {moment(message.latest_message_created_at).fromNow(true)}
          </span>
        </div>
      </div>

      <div className='separator separator-dashed'></div>
    </div>
  )
}

const HeaderMessagesMenu: FC = () => {
  const dispatch = useDispatch()
  const intl = useIntl()

  const user: UserModel = useSelector<RootState>(
    ({userReducer}) => userReducer.user,
    shallowEqual
  ) as UserModel

  const [newMessagesCount, setNewMessagesCount] = useState(0)
  const [messages, setMessages] = useState<any[]>([])

  const loadUserMessages = () => {
    const onSuccess = (res: any) => {
      const unreadCount = res?.result?.unreadMessageCount ?? 0
      setNewMessagesCount(unreadCount)
      setMessages(res?.result?.messages?.filter((message: any) => message?.sender) ?? [])

      // Update navbar badge
      const badge = document.getElementById('chat-unread-badge')
      if (badge) {
        if (unreadCount > 0) {
          badge.style.display = 'block'
        } else {
          badge.style.display = 'none'
        }
      }
    }

    const onFailure = (err: any) => {
      console.log({err})
    }

    dispatch(getUserMessages(onSuccess, onFailure))
  }

  useEffect(() => {
    if (!user?.id) return

    loadUserMessages()

    // Listen for new messages to refresh the list
    const handleNewMessage = () => {
      loadUserMessages()
    }

    // Listen for messages read events
    const handleMessagesRead = () => {
      loadUserMessages()
    }

    socket.on('new-message', handleNewMessage)
    socket.on('messages-read', handleMessagesRead)

    return () => {
      socket.off('new-message', handleNewMessage)
      socket.off('messages-read', handleMessagesRead)
      setNewMessagesCount(0)
      setMessages([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px'
      data-kt-menu='true'
    >
      <div
        className='d-flex flex-column bgi-no-repeat rounded-top'
        style={{backgroundImage: `url('${toAbsoluteUrl('/media/misc/pattern-1.jpg')}')`}}
      >
        <h3 className='text-white fw-bold px-9 mt-10 mb-6'>
          {newMessagesCount > 0 ? (
            <>
              <FormattedMessage
                id='MESSAGE.NEW_MESSAGE_COUNT'
                values={{count: <strong>{newMessagesCount}</strong>}}
              />
            </>
          ) : (
            intl.formatMessage({id: 'MESSAGE.NO_NEW_MESSAGE'})
          )}
        </h3>
      </div>

      <div className='tab-content'>
        <div className='tab-pane fade show active' id='kt_topbar_notifications_1' role='tabpanel'>
          <div className='scroll-y mh-325px my-5 px-8'>
            <div className='card-body pt-5' id='kt_chat_contacts_body'>
              <div
                className='me-n5 pe-5 h-200px h-lg-auto'
                data-kt-scroll='true'
                data-kt-scroll-activate='{default: false, lg: true}'
                data-kt-scroll-max-height='auto'
                data-kt-scroll-dependencies='#kt_header, #kt_toolbar, #kt_footer, #kt_chat_contacts_header'
                data-kt-scroll-wrappers='#kt_content, #kt_chat_contacts_body'
                data-kt-scroll-offset='0px'
              >
                {messages.map((message, i) => (
                  <Message message={message} user={user} key={i} />
                ))}
              </div>
            </div>
          </div>

          <div className='py-3 text-center border-top'>
            <Link
              // to='/institute/message/messages/index'
              to='/messages'
              className='btn btn-color-gray-600 btn-active-color-primary'
            >
              {intl.formatMessage({id: 'GENERAL.VIEW_ALL'})}
              <span className='fa fa-arrow-right ms-1' />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export {HeaderMessagesMenu}
